/**
 * Loads the M7 story data (plan S0): Zod-parses every `src/data/story/**` file, merges the per-thread evidence files
 * and the per-mission and per-file comms (plan D-TBD: split so content builders own disjoint files), and checks the
 * cross-file rules: unique log and node ids, threads matching their file, boot logs outside the clue budget, comms
 * links and "Show" logs resolving, mission steps and forks consistent, callback triggers known, and the one-way
 * ladder's shape (nothing returns to Blackmail, Sell is final). Throws an Error naming the file and the path.
 *
 * Content tests (the clue budget, word limits, slot candidates) belong to the content tasks; this file checks only
 * what every consumer relies on.
 */
import type { z } from 'zod'
import callbacksJson from '../data/story/callbacks.json'
import caseJson from '../data/story/case.json'
import claimsJson from '../data/story/claims.json'
import clockJson from '../data/story/clock.json'
import commsCommonJson from '../data/story/comms/common.json'
import commsFinaleJson from '../data/story/comms/finale.json'
import commsM1Json from '../data/story/comms/m1.json'
import commsM2Json from '../data/story/comms/m2.json'
import commsM3Json from '../data/story/comms/m3.json'
import commsM4Json from '../data/story/comms/m4.json'
import bootJson from '../data/story/evidence/boot.json'
import evacuationJson from '../data/story/evidence/evacuation.json'
import reportJson from '../data/story/evidence/report.json'
import settlementJson from '../data/story/evidence/settlement.json'
import watchJson from '../data/story/evidence/watch.json'
import finaleJson from '../data/story/finale.json'
import leverageJson from '../data/story/leverage.json'
import loggingJson from '../data/story/logging.json'
import m1Json from '../data/story/missions/m1.json'
import m2Json from '../data/story/missions/m2.json'
import m3Json from '../data/story/missions/m3.json'
import m4Json from '../data/story/missions/m4.json'
import reactivationJson from '../data/story/reactivation.json'
import {
  callbacksFileSchema,
  caseFileSchema,
  claimsFileSchema,
  clockFileSchema,
  commsFileSchema,
  evidenceFileSchema,
  finaleFileSchema,
  leverageFileSchema,
  loggingFileSchema,
  missionFileSchema,
  reactivationFileSchema,
  type CaseConfig,
  type ClaimsConfig,
  type ClockConfig,
  type CommsNode,
  type EvidenceLog,
  type FinaleConfig,
  type LeverageConfig,
  type LoggingConfig,
  type MissionDef,
  type ReactivationConfig,
  type StoryCallback,
} from './schemas'
import {
  COMMS_FILES,
  EVIDENCE_FILES,
  MISSION_IDS,
  STORY_FLAGS,
  STORY_TRIGGERS,
  type CommsFile,
  type EvidenceFile,
  type MissionId,
} from './types'

/** The raw JSON inputs (tests pass their own). */
export interface StoryRawFiles {
  logging: unknown
  evidence: Readonly<Record<EvidenceFile, unknown>>
  claims: unknown
  case: unknown
  clock: unknown
  leverage: unknown
  finale: unknown
  callbacks: unknown
  reactivation: unknown
  missions: Readonly<Record<MissionId, unknown>>
  comms: Readonly<Record<CommsFile, unknown>>
}

export const STORY_RAW_FILES: StoryRawFiles = {
  logging: loggingJson,
  evidence: { evacuation: evacuationJson, settlement: settlementJson, watch: watchJson, report: reportJson, boot: bootJson },
  claims: claimsJson,
  case: caseJson,
  clock: clockJson,
  leverage: leverageJson,
  finale: finaleJson,
  callbacks: callbacksJson,
  reactivation: reactivationJson,
  missions: { m1: m1Json, m2: m2Json, m3: m3Json, m4: m4Json },
  comms: {
    common: commsCommonJson,
    m1: commsM1Json,
    m2: commsM2Json,
    m3: commsM3Json,
    m4: commsM4Json,
    finale: commsFinaleJson,
  },
}

export interface StoryData {
  logging: LoggingConfig
  /** Every authored log, in `EVIDENCE_FILES` order. */
  evidence: readonly EvidenceLog[]
  evidenceByFile: Readonly<Record<EvidenceFile, readonly EvidenceLog[]>>
  logsById: ReadonlyMap<string, EvidenceLog>
  claims: ClaimsConfig
  case: CaseConfig
  clock: ClockConfig
  leverage: LeverageConfig
  finale: FinaleConfig
  callbacks: readonly StoryCallback[]
  reactivation: ReactivationConfig
  missions: Readonly<Record<MissionId, MissionDef>>
  /** Every comms node, in `COMMS_FILES` order. */
  comms: readonly CommsNode[]
  commsById: ReadonlyMap<string, CommsNode>
}

function parseFile<S extends z.ZodType>(file: string, schema: S, raw: unknown): z.output<S> {
  const result = schema.safeParse(raw)
  if (result.success) return result.data
  const issue = result.error.issues[0]
  const where = issue && issue.path.length > 0 ? issue.path.map(String).join('.') : '(root)'
  throw new Error(`story data: ${file}: ${where}: ${issue?.message ?? 'invalid'}`)
}

function fail(file: string, message: string): never {
  throw new Error(`story data: ${file}: ${message}`)
}

const THREAD_FILES: ReadonlySet<string> = new Set(['evacuation', 'settlement', 'watch', 'report'])

function loadEvidence(raw: StoryRawFiles['evidence']): {
  byFile: Record<EvidenceFile, readonly EvidenceLog[]>
  all: EvidenceLog[]
  byId: Map<string, EvidenceLog>
} {
  const byFile = {} as Record<EvidenceFile, readonly EvidenceLog[]>
  const all: EvidenceLog[] = []
  const byId = new Map<string, EvidenceLog>()
  for (const f of EVIDENCE_FILES) {
    const file = `evidence/${f}.json`
    const logs = parseFile(file, evidenceFileSchema, raw[f]).logs
    logs.forEach((log, i) => {
      if (byId.has(log.id)) fail(file, `logs.${i}.id: duplicate log id '${log.id}'`)
      if (THREAD_FILES.has(f) && log.thread !== f) fail(file, `logs.${i}.thread: '${log.thread}' in the ${f} file`)
      if (f === 'boot' && (log.source !== 'frStationBootLog' || !log.outsideBudget)) {
        fail(file, `logs.${i}: a boot log has source frStationBootLog and outsideBudget true`)
      }
      if (f !== 'boot' && log.outsideBudget) fail(file, `logs.${i}.outsideBudget: only boot logs sit outside the budget`)
      byId.set(log.id, log)
      all.push(log)
    })
    byFile[f] = logs
  }
  return { byFile, all, byId }
}

function loadMissions(raw: StoryRawFiles['missions']): Record<MissionId, MissionDef> {
  const out = {} as Record<MissionId, MissionDef>
  for (const id of MISSION_IDS) {
    const file = `missions/${id}.json`
    const m = parseFile(file, missionFileSchema, raw[id])
    if (m.id !== id) fail(file, `id: '${m.id}' in the ${id} file`)
    const steps = new Set<string>()
    m.steps.forEach((s, i) => {
      if (steps.has(s.id)) fail(file, `steps.${i}.id: duplicate step id '${s.id}'`)
      steps.add(s.id)
    })
    const forks = new Set<string>()
    m.forks.forEach((fk, i) => {
      if (forks.has(fk.id)) fail(file, `forks.${i}.id: duplicate fork id '${fk.id}'`)
      forks.add(fk.id)
      if (!steps.has(fk.afterStep)) fail(file, `forks.${i}.afterStep: no step '${fk.afterStep}'`)
      const choices = new Set<string>()
      fk.choices.forEach((c, j) => {
        if (choices.has(c.id)) fail(file, `forks.${i}.choices.${j}.id: duplicate choice id '${c.id}'`)
        choices.add(c.id)
      })
    })
    out[id] = m
  }
  return out
}

function loadComms(raw: StoryRawFiles['comms'], logsById: ReadonlyMap<string, EvidenceLog>): { all: CommsNode[]; byId: Map<string, CommsNode> } {
  const all: CommsNode[] = []
  const byId = new Map<string, CommsNode>()
  const fileOf = new Map<string, string>()
  for (const f of COMMS_FILES) {
    const file = `comms/${f}.json`
    parseFile(file, commsFileSchema, raw[f]).nodes.forEach((node, i) => {
      if (byId.has(node.id)) fail(file, `nodes.${i}.id: duplicate node id '${node.id}' (also in ${fileOf.get(node.id) ?? '?'})`)
      byId.set(node.id, node)
      fileOf.set(node.id, file)
      all.push(node)
    })
  }
  for (const node of all) {
    const file = fileOf.get(node.id) ?? 'comms'
    const replies = new Set<string>()
    node.replies.forEach((r, j) => {
      if (replies.has(r.id)) fail(file, `node '${node.id}' replies.${j}.id: duplicate reply id '${r.id}'`)
      replies.add(r.id)
      if (r.next !== undefined && !byId.has(r.next)) fail(file, `node '${node.id}' replies.${j}.next: no node '${r.next}'`)
      for (const logId of r.show?.logs ?? []) {
        if (!logsById.has(logId)) fail(file, `node '${node.id}' replies.${j}.show.logs: no log '${logId}'`)
      }
    })
  }
  return { all, byId }
}

const CALLBACK_TRIGGERS: ReadonlySet<string> = new Set<string>([...STORY_FLAGS, ...STORY_TRIGGERS])

function loadCallbacks(raw: unknown): StoryCallback[] {
  const file = 'callbacks.json'
  const callbacks = parseFile(file, callbacksFileSchema, raw).callbacks
  const ids = new Set<string>()
  callbacks.forEach((cb, i) => {
    if (ids.has(cb.id)) fail(file, `callbacks.${i}.id: duplicate callback id '${cb.id}'`)
    ids.add(cb.id)
    if (!CALLBACK_TRIGGERS.has(cb.flag)) fail(file, `callbacks.${i}.flag: '${cb.flag}' is not a story flag or trigger`)
    if (cb.channel === 'wire' && cb.source === undefined) fail(file, `callbacks.${i}.source: a Wire callback names its outlet`)
  })
  return callbacks
}

function checkFinale(f: FinaleConfig): void {
  const file = 'finale.json'
  const seen = new Set<string>()
  f.ladder.forEach((m, i) => {
    const key = `${m.from}>${m.to}`
    if (seen.has(key)) fail(file, `ladder.${i}: duplicate move ${key}`)
    seen.add(key)
    if (m.from === m.to) fail(file, `ladder.${i}: a path can't move to itself`)
    if (m.to === 'blackmail') fail(file, `ladder.${i}: nothing returns to Blackmail (spec §8.3)`)
    if (m.from === 'sell') fail(file, `ladder.${i}: Sell is final (spec §8.3)`)
  })
  if (f.epilogue.minItems > f.epilogue.maxItems) fail(file, 'epilogue: minItems > maxItems')
}

function checkClaims(c: ClaimsConfig): void {
  const tiers = new Set<number>()
  c.templates.forEach((t, i) => {
    if (tiers.has(t.tier)) fail('claims.json', `templates.${i}.tier: a second template for tier ${t.tier}`)
    tiers.add(t.tier)
  })
}

function checkCase(c: CaseConfig): void {
  const { circumstantial, documented, damning } = c.caseLine
  if (!(circumstantial < documented && documented < damning)) fail('case.json', 'caseLine: thresholds must rise')
}

function checkClock(c: ClockConfig): void {
  c.tierRows.forEach((row, i) => {
    if (row.tier !== i + 3) fail('clock.json', `tierRows.${i}.tier: rows are tiers 3, 4, 5 in order`)
  })
}

function checkReactivation(r: ReactivationConfig): void {
  const s = r.eligibility.classShares
  const sum = s.habRing + s.oreProcessor + s.depot + s.lab
  if (Math.abs(sum - 1) > 1e-6) fail('reactivation.json', `eligibility.classShares: shares sum to ${sum}, not 1`)
  if (r.bunker.minT > r.bunker.maxT) fail('reactivation.json', 'bunker: minT > maxT')
}

/** Parses and cross-checks every story data file. Pure: the result depends only on `raw`. */
export function loadStoryData(raw: StoryRawFiles = STORY_RAW_FILES): StoryData {
  const logging = parseFile('logging.json', loggingFileSchema, raw.logging)
  const evidence = loadEvidence(raw.evidence)
  const claims = parseFile('claims.json', claimsFileSchema, raw.claims)
  checkClaims(claims)
  const caseCfg = parseFile('case.json', caseFileSchema, raw.case)
  checkCase(caseCfg)
  const clock = parseFile('clock.json', clockFileSchema, raw.clock)
  checkClock(clock)
  const leverage = parseFile('leverage.json', leverageFileSchema, raw.leverage)
  const finale = parseFile('finale.json', finaleFileSchema, raw.finale)
  checkFinale(finale)
  const callbacks = loadCallbacks(raw.callbacks)
  const reactivation = parseFile('reactivation.json', reactivationFileSchema, raw.reactivation)
  checkReactivation(reactivation)
  const missions = loadMissions(raw.missions)
  const comms = loadComms(raw.comms, evidence.byId)
  return {
    logging,
    evidence: evidence.all,
    evidenceByFile: evidence.byFile,
    logsById: evidence.byId,
    claims,
    case: caseCfg,
    clock,
    leverage,
    finale,
    callbacks,
    reactivation,
    missions,
    comms: comms.all,
    commsById: comms.byId,
  }
}

let cached: StoryData | undefined

/** The shipped story data, parsed once on first use (story logic is event-driven, so a lazy load is fine). */
export function storyData(): StoryData {
  cached ??= loadStoryData()
  return cached
}
